import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  Filter,
  Plus,
  Printer,
  Download,
  Search,
  Pencil,
  Trash2,
  PackagePlus,
  HandHelping,
  RotateCcw,
  X
} from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";
import "./LibraryManagement.css";

type BookTab = "all" | "borrowed" | "available" | "low";

interface LibraryBook {
  id: number;
  isbn: string;
  title: string;
  author?: string;
  publisher?: string;
  shelf?: string;
  quantity: number;
  borrowed: number;
  available: number;
  category?: string;
  description?: string;
}

interface BorrowRecord {
  id: number;
  bookId: number;
  bookTitle: string;
  isbn?: string;
  borrowerId: number;
  borrowerName?: string;
  borrowerRole?: string;
  quantity: number;
  borrowDate?: string;
  expectedReturnDate?: string;
  returnDate?: string;
  status: string;
}

interface SummaryTotals {
  totalTitles: number;
  totalQuantity: number;
  totalBorrowed: number;
  available: number;
  overdue: number;
}

interface UserOption {
  id: number;
  name: string;
  role: string;
}

const TAB_LABELS: { key: BookTab; label: string }[] = [
  { key: "all", label: "All Books" },
  { key: "borrowed", label: "Borrowed Books" },
  { key: "available", label: "Available Books" },
  { key: "low", label: "Low Stock" }
];

const emptyBookForm = {
  isbn: "",
  title: "",
  author: "",
  publisher: "",
  shelf: "",
  quantity: "0",
  category: "",
  description: ""
};

const LibraryManagement: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const canManage = role === "admin" || role === "headteacher" || role === "superadmin";
  const canReviewBorrowed = canManage;
  const canBorrowBooks = role === "teacher" || canManage;
  const visibleTabs = useMemo(
    () =>
      TAB_LABELS.filter((tab) => {
        if (!canReviewBorrowed && tab.key === "borrowed") return false;
        if (!canManage && tab.key === "low") return false;
        return true;
      }),
    [canManage, canReviewBorrowed]
  );
  const pageTitle = canManage ? "Library Management" : "Library Catalogue";
  const pageSubtitle = canManage
    ? "Track books, borrowing, and returns in one modern workspace."
    : "Browse the school catalogue and borrow available books without stock-management controls.";

  const [activeTab, setActiveTab] = useState<BookTab>("all");
  const [books, setBooks] = useState<LibraryBook[]>([]);
  const [records, setRecords] = useState<BorrowRecord[]>([]);
  const [summary, setSummary] = useState<SummaryTotals>({
    totalTitles: 0,
    totalQuantity: 0,
    totalBorrowed: 0,
    available: 0,
    overdue: 0
  });
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useAutoDismiss(message, setMessage, null, 5000);

  const [searchTerm, setSearchTerm] = useState("");
  const [borrowStatus, setBorrowStatus] = useState("borrowed");

  const [bookModalOpen, setBookModalOpen] = useState(false);
  const [editingBook, setEditingBook] = useState<LibraryBook | null>(null);
  const [bookForm, setBookForm] = useState(emptyBookForm);

  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [stockTarget, setStockTarget] = useState<LibraryBook | null>(null);
  const [stockQty, setStockQty] = useState("1");
  const [stockNote, setStockNote] = useState("");

  const [borrowModalOpen, setBorrowModalOpen] = useState(false);
  const [borrowTarget, setBorrowTarget] = useState<LibraryBook | null>(null);
  const [borrowForm, setBorrowForm] = useState({
    borrowerId: "",
    quantity: "1",
    borrowDate: new Date().toISOString().slice(0, 10),
    expectedReturnDate: ""
  });

  const formatDate = (value?: string) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString();
  };

  const fetchSummary = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(getApiUrl("library/summary"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(res.data?.totals || summary);
    } catch {
      setSummary({
        totalTitles: 0,
        totalQuantity: 0,
        totalBorrowed: 0,
        available: 0,
        overdue: 0
      });
    }
  }, [token, summary]);

  const fetchBooks = useCallback(async () => {
    if (!token) return;
    if (activeTab === "borrowed") return;
    setLoading(true);
    setMessage(null);
    try {
      const params: Record<string, string> = {};
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (activeTab === "available") params.status = "available";
      if (activeTab === "low") params.status = "low";
      const res = await axios.get(getApiUrl("library/books"), {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setBooks(res.data?.books || []);
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to load books.");
      setBooks([]);
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, searchTerm]);

  const fetchBorrowed = useCallback(async () => {
    if (!token) return;
    if (activeTab !== "borrowed") return;
    setLoading(true);
    setMessage(null);
    try {
      const params: Record<string, string> = {};
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (borrowStatus) params.status = borrowStatus;
      const res = await axios.get(getApiUrl("library/borrowed"), {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setRecords(res.data?.records || []);
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to load borrowed records.");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [token, activeTab, searchTerm, borrowStatus]);

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(getApiUrl("users/all"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allUsers = res.data?.users || res.data || [];
      const filtered = allUsers.filter((u: any) => ["student", "teacher", "headteacher", "admin"].includes((u.role || "").toLowerCase()));
      setUsers(
        filtered.map((u: any) => ({
          id: Number(u.id),
          name: u.name || u.fullName || `User ${u.id}`,
          role: (u.role || "student").toLowerCase()
        }))
      );
    } catch {
      setUsers([]);
    }
  }, [token]);

  useEffect(() => {
    fetchSummary();
    fetchUsers();
  }, [fetchSummary, fetchUsers]);

  useEffect(() => {
    fetchBooks();
    fetchBorrowed();
  }, [fetchBooks, fetchBorrowed]);

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(visibleTabs[0]?.key || "all");
    }
  }, [activeTab, visibleTabs]);

  const openBookModal = (book?: LibraryBook) => {
    if (book) {
      setEditingBook(book);
      setBookForm({
        isbn: book.isbn || "",
        title: book.title || "",
        author: book.author || "",
        publisher: book.publisher || "",
        shelf: book.shelf || "",
        quantity: String(book.quantity || 0),
        category: book.category || "",
        description: book.description || ""
      });
    } else {
      setEditingBook(null);
      setBookForm(emptyBookForm);
    }
    setBookModalOpen(true);
  };

  const saveBook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setLoading(true);
    try {
      const payload = {
        ...bookForm,
        quantity: Number(bookForm.quantity || 0)
      };
      if (editingBook) {
        await axios.put(getApiUrl(`library/books/${editingBook.id}`), payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(getApiUrl("library/books"), payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setBookModalOpen(false);
      fetchBooks();
      fetchSummary();
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to save book.");
    } finally {
      setLoading(false);
    }
  };

  const archiveBook = async (bookId: number) => {
    if (!token || !canManage) return;
    if (!window.confirm("Archive this book?")) return;
    try {
      await axios.delete(getApiUrl(`library/books/${bookId}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBooks();
      fetchSummary();
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to archive book.");
    }
  };

  const openStockModal = (book: LibraryBook) => {
    setStockTarget(book);
    setStockQty("1");
    setStockNote("");
    setStockModalOpen(true);
  };

  const addStock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !stockTarget) return;
    setLoading(true);
    try {
      await axios.post(
        getApiUrl(`library/books/${stockTarget.id}/stock`),
        { quantity: Number(stockQty || 0), note: stockNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStockModalOpen(false);
      fetchBooks();
      fetchSummary();
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to update stock.");
    } finally {
      setLoading(false);
    }
  };

  const openBorrowModal = (book: LibraryBook) => {
    setBorrowTarget(book);
    setBorrowForm({
      borrowerId: "",
      quantity: "1",
      borrowDate: new Date().toISOString().slice(0, 10),
      expectedReturnDate: ""
    });
    setBorrowModalOpen(true);
  };

  const borrowBook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !borrowTarget) return;
    setLoading(true);
    try {
      const borrower = users.find((u) => String(u.id) === borrowForm.borrowerId);
      await axios.post(
        getApiUrl("library/borrow"),
        {
          bookId: borrowTarget.id,
          borrowerId: borrowForm.borrowerId,
          borrowerRole: borrower?.role || "student",
          quantity: Number(borrowForm.quantity || 0),
          borrowDate: borrowForm.borrowDate,
          expectedReturnDate: borrowForm.expectedReturnDate || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBorrowModalOpen(false);
      fetchBooks();
      fetchBorrowed();
      fetchSummary();
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to borrow book.");
    } finally {
      setLoading(false);
    }
  };

  const markReturned = async (recordId: number) => {
    if (!token) return;
    setLoading(true);
    try {
      await axios.post(
        getApiUrl(`library/return/${recordId}`),
        { returnDate: new Date().toISOString().slice(0, 10) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchBorrowed();
      fetchBooks();
      fetchSummary();
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Failed to mark returned.");
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    const rows = activeTab === "borrowed" ? records : books;
    if (!rows.length) return;
    const headers =
      activeTab === "borrowed"
        ? ["Book", "Borrower", "Role", "Quantity", "Borrow Date", "Return Date", "Status"]
        : ["ISBN", "Title", "Author", "Publisher", "Shelf", "Quantity", "Borrowed", "Available"];
    const csvRows = [
      headers.join(","),
      ...rows.map((row: any) =>
        activeTab === "borrowed"
          ? [
              `"${row.bookTitle || ""}"`,
              `"${row.borrowerName || ""}"`,
              `"${row.borrowerRole || ""}"`,
              row.quantity,
              `"${row.borrowDate || ""}"`,
              `"${row.returnDate || ""}"`,
              `"${row.status || ""}"`
            ].join(",")
          : [
              `"${row.isbn || ""}"`,
              `"${row.title || ""}"`,
              `"${row.author || ""}"`,
              `"${row.publisher || ""}"`,
              `"${row.shelf || ""}"`,
              row.quantity,
              row.borrowed,
              row.available
            ].join(",")
      )
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "library-export.csv";
    link.click();
  };

  const filteredBorrowRecords = useMemo(() => {
    if (borrowStatus !== "overdue") return records;
    const now = new Date();
    return records.filter((rec) => rec.expectedReturnDate && new Date(rec.expectedReturnDate) < now && rec.status === "borrowed");
  }, [records, borrowStatus]);

  return (
    <div className="utopia-page">
      <div className="utopia-page-header">
        <div>
          <h1 className="utopia-page-title library-title">{pageTitle}</h1>
          <p className="page-subtitle">{pageSubtitle}</p>
        </div>
        <div className="flex w-full flex-col gap-3 xl:items-end">
          <div className="library-tab-list">
            {visibleTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`library-tab-button ${
                  activeTab === tab.key
                    ? "library-tab-button-active"
                    : "library-tab-button-idle"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canManage && (
              <button className="utopia-btn-primary flex items-center gap-2" onClick={() => openBookModal()}>
                <Plus size={16} /> Add New Book
              </button>
            )}
            <button className="utopia-btn-secondary flex items-center gap-2" onClick={() => window.print()}>
              <Printer size={16} /> Print
            </button>
            <button className="utopia-btn-secondary flex items-center gap-2" onClick={exportCsv}>
              <Download size={16} /> Export
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="utopia-card p-4">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Total Titles</p>
          <p className="text-2xl font-black text-slate-800">{summary.totalTitles}</p>
        </div>
        <div className="utopia-card p-4">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Total Borrowed</p>
          <p className="text-2xl font-black text-amber-600">{summary.totalBorrowed}</p>
        </div>
        <div className="utopia-card p-4">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Available Stock</p>
          <p className="text-2xl font-black text-emerald-600">{summary.available}</p>
        </div>
        <div className="utopia-card p-4">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-bold">Overdue Books</p>
          <p className="text-2xl font-black text-rose-600">{summary.overdue}</p>
        </div>
      </div>

      {message && <div className="mb-6 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{message}</div>}

      <div className="utopia-card p-4 mb-6">
        <div className="flex items-center gap-2 text-slate-600 text-sm font-bold mb-3">
          <Filter size={16} /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Search</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="utopia-input pl-8"
                placeholder={activeTab === "borrowed" ? "Search borrower or book" : "Search ISBN, title, author"}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          {activeTab === "borrowed" && canReviewBorrowed && (
            <div>
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Status</label>
              <select className="utopia-input" value={borrowStatus} onChange={(e) => setBorrowStatus(e.target.value)}>
                <option value="borrowed">Borrowed</option>
                <option value="returned">Returned</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
          )}
          <div className="flex items-end gap-2">
            <button className="utopia-btn-secondary w-full" onClick={() => {
              setSearchTerm("");
              setBorrowStatus("borrowed");
            }}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {activeTab !== "borrowed" && (
        <div className="utopia-card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="library-section-title"><BookOpen size={18} /> Books Inventory</h3>
            <span className="library-section-count">{loading ? "Loading..." : `${books.length} books`}</span>
          </div>
          {books.length === 0 ? (
            <div className="text-sm text-slate-400">No books found. Add a book to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="library-table">
                <thead>
                  <tr className="library-table-head-row">
                    <th className="py-2">ISBN</th>
                    <th className="py-2">Title</th>
                    <th className="py-2">Author</th>
                    <th className="py-2">Publisher</th>
                    <th className="py-2">Shelf</th>
                    <th className="py-2">Quantity</th>
                    <th className="py-2">Borrowed</th>
                    <th className="py-2">Available</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {books.map((book) => (
                    <tr key={book.id} className="border-t border-slate-100">
                      <td className="py-2">{book.isbn}</td>
                      <td className="py-2 font-semibold">{book.title}</td>
                      <td className="py-2">{book.author || "-"}</td>
                      <td className="py-2">{book.publisher || "-"}</td>
                      <td className="py-2">{book.shelf || "-"}</td>
                      <td className="py-2">{book.quantity}</td>
                      <td className="py-2">{book.borrowed}</td>
                      <td className="py-2 font-semibold">{book.available}</td>
                      <td className="py-2 text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          {canManage && (
                            <button className="library-action-button" onClick={() => openBookModal(book)}>
                              <Pencil size={14} /> Edit
                            </button>
                          )}
                          {canManage && (
                            <button className="library-action-button" onClick={() => archiveBook(book.id)}>
                              <Trash2 size={14} /> Archive
                            </button>
                          )}
                          {canBorrowBooks && (
                            <button
                              className="library-action-button library-action-button-borrow"
                              onClick={() => openBorrowModal(book)}
                            >
                              <HandHelping size={14} /> Borrow
                            </button>
                          )}
                          {canManage && (
                            <button className="library-action-button" onClick={() => openStockModal(book)}>
                              <PackagePlus size={14} /> Add Stock
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "borrowed" && canReviewBorrowed && (
        <div className="utopia-card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold flex items-center gap-2"><RotateCcw size={18} /> Borrowed Books</h3>
            <span className="text-sm text-slate-500">{loading ? "Loading..." : `${filteredBorrowRecords.length} records`}</span>
          </div>
          {filteredBorrowRecords.length === 0 ? (
            <div className="text-sm text-slate-400">No borrowed records found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="py-2">Book</th>
                    <th className="py-2">Borrower</th>
                    <th className="py-2">Role</th>
                    <th className="py-2">Quantity</th>
                    <th className="py-2">Borrow Date</th>
                    <th className="py-2">Return Date</th>
                    <th className="py-2">Status</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBorrowRecords.map((record) => (
                    <tr key={record.id} className="border-t border-slate-100">
                      <td className="py-2 font-semibold">{record.bookTitle}</td>
                      <td className="py-2">{record.borrowerName || "Unknown"}</td>
                      <td className="py-2 capitalize">{record.borrowerRole || "-"}</td>
                      <td className="py-2">{record.quantity}</td>
                      <td className="py-2">{formatDate(record.borrowDate)}</td>
                      <td className="py-2">{formatDate(record.returnDate || record.expectedReturnDate)}</td>
                      <td className="py-2">
                        <span className={`library-pill ${record.status}`}>{record.status}</span>
                      </td>
                      <td className="py-2 text-right">
                        {record.status === "borrowed" && canManage && (
                          <button className="utopia-btn-primary h-8" onClick={() => markReturned(record.id)}>
                            Mark Returned
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {bookModalOpen && (
        <div className="utopia-modal-overlay">
          <div className="utopia-modal">
            <div className="utopia-modal-header">
              <div>
                <h3 className="text-lg font-bold">{editingBook ? "Edit Book" : "Add New Book"}</h3>
                <p className="text-sm text-slate-500">Provide book details and stock information.</p>
              </div>
              <button className="utopia-btn-icon" onClick={() => setBookModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={saveBook}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">ISBN</label>
                  <input className="utopia-input" value={bookForm.isbn} onChange={(e) => setBookForm((prev) => ({ ...prev, isbn: e.target.value }))} required />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Title</label>
                  <input className="utopia-input" value={bookForm.title} onChange={(e) => setBookForm((prev) => ({ ...prev, title: e.target.value }))} required />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Author</label>
                  <input className="utopia-input" value={bookForm.author} onChange={(e) => setBookForm((prev) => ({ ...prev, author: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Publisher</label>
                  <input className="utopia-input" value={bookForm.publisher} onChange={(e) => setBookForm((prev) => ({ ...prev, publisher: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Shelf</label>
                  <input className="utopia-input" value={bookForm.shelf} onChange={(e) => setBookForm((prev) => ({ ...prev, shelf: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Quantity</label>
                  <input className="utopia-input" type="number" min="0" value={bookForm.quantity} onChange={(e) => setBookForm((prev) => ({ ...prev, quantity: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Category</label>
                  <input className="utopia-input" value={bookForm.category} onChange={(e) => setBookForm((prev) => ({ ...prev, category: e.target.value }))} />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Description</label>
                  <textarea className="utopia-input h-24" value={bookForm.description} onChange={(e) => setBookForm((prev) => ({ ...prev, description: e.target.value }))} />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" className="utopia-btn-secondary" onClick={() => setBookModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="utopia-btn-primary" disabled={loading}>
                  {editingBook ? "Save Changes" : "Add Book"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {stockModalOpen && stockTarget && (
        <div className="utopia-modal-overlay">
          <div className="utopia-modal">
            <div className="utopia-modal-header">
              <div>
                <h3 className="text-lg font-bold">Add Stock</h3>
                <p className="text-sm text-slate-500">{stockTarget.title}</p>
              </div>
              <button className="utopia-btn-icon" onClick={() => setStockModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={addStock}>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Quantity</label>
                  <input className="utopia-input" type="number" min="1" value={stockQty} onChange={(e) => setStockQty(e.target.value)} required />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Note</label>
                  <input className="utopia-input" value={stockNote} onChange={(e) => setStockNote(e.target.value)} placeholder="Stock adjustment note" />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" className="utopia-btn-secondary" onClick={() => setStockModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="utopia-btn-primary" disabled={loading}>
                  Add Stock
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {borrowModalOpen && borrowTarget && (
        <div className="utopia-modal-overlay">
          <div className="utopia-modal">
            <div className="utopia-modal-header">
              <div>
                <h3 className="text-lg font-bold">Borrow Book</h3>
                <p className="text-sm text-slate-500">{borrowTarget.title}</p>
              </div>
              <button className="utopia-btn-icon" onClick={() => setBorrowModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={borrowBook}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Borrower</label>
                  <select className="utopia-input" value={borrowForm.borrowerId} onChange={(e) => setBorrowForm((prev) => ({ ...prev, borrowerId: e.target.value }))} required>
                    <option value="">Select borrower</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>{user.name} ({user.role})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Quantity</label>
                  <input className="utopia-input" type="number" min="1" max={borrowTarget.available} value={borrowForm.quantity} onChange={(e) => setBorrowForm((prev) => ({ ...prev, quantity: e.target.value }))} required />
                  <p className="text-xs text-slate-500 mt-1">Available: {borrowTarget.available}</p>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Borrow Date</label>
                  <input className="utopia-input" type="date" value={borrowForm.borrowDate} onChange={(e) => setBorrowForm((prev) => ({ ...prev, borrowDate: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Expected Return</label>
                  <input className="utopia-input" type="date" value={borrowForm.expectedReturnDate} onChange={(e) => setBorrowForm((prev) => ({ ...prev, expectedReturnDate: e.target.value }))} />
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button type="button" className="utopia-btn-secondary" onClick={() => setBorrowModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="utopia-btn-primary" disabled={loading}>
                  Borrow Book
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LibraryManagement;
